import SaveButton from "components/shared/SaveButton";
import StyledTextField from "components/shared/StyledTextField/StyledTextField";
import TitleSeparation from "components/shared/TitleSeparation";
import { BodyHighlight, SmallBody } from "helpers/TypographyComponents";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import type { CalculationBaseColumn } from "store/calculationBaseColumnLabelsStore";
import {
    CALCULATION_BASE_COLUMNS,
    deleteCalculationBaseColumnLabel,
    getCalculationBaseColumnLabels,
    upsertCalculationBaseColumnLabel,
} from "store/calculationBaseColumnLabelsStore";
import { useAppDispatch, useAppSelector } from "store/store";

import styles from "./style.module.scss";

const READ_ONLY_COLUMNS = new Set<CalculationBaseColumn>(["quantity"]);
const EDITABLE_COLUMNS = CALCULATION_BASE_COLUMNS.filter((column) => !READ_ONLY_COLUMNS.has(column));
/** Custom CA columns — without a label they stay off sales import and contract pickers. */
const OPTIONAL_LABEL_COLUMNS = new Set<CalculationBaseColumn>(["turnover_1", "turnover_2"]);

const CALCULATION_STEPS = [
    { titleKey: "StepImportTitle", descriptionKey: "StepImportDescription" },
    { titleKey: "StepContractTitle", descriptionKey: "StepContractDescription" },
    { titleKey: "StepCalculationTitle", descriptionKey: "StepCalculationDescription" },
] as const;

/**
 * Settings screen to configure company-specific labels for the sales columns that can be used
 * as a contract's calculation base (quantity, turnover, turnover_1, turnover_2).
 */
const CalculationBaseColumnLabels = () => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();

    const { labels, labelsStatus } = useAppSelector((state) => state.calculationBaseColumnLabels);

    // Ephemeral edit buffer keyed by column; seeded from the fetched labels.
    const [drafts, setDrafts] = useState<Record<string, string>>(() =>
        Object.fromEntries(labels.map((label) => [label.calculationBaseColumn, label.label])),
    );
    const [isSaving, setIsSaving] = useState(false);
    // Avoid flashing the "inactive" state while drafts are still empty before the fetch lands.
    const labelsLoaded = labelsStatus === "succeeded" || labelsStatus === "failed";

    useEffect(() => {
        if (labelsStatus === "idle") {
            dispatch(getCalculationBaseColumnLabels());
        }
    }, [labelsStatus, dispatch]);

    useEffect(() => {
        setDrafts(Object.fromEntries(labels.map((label) => [label.calculationBaseColumn, label.label])));
    }, [labels]);

    const savedLabels = useMemo(
        () =>
            Object.fromEntries(
                CALCULATION_BASE_COLUMNS.map((column) => [
                    column,
                    labels.find((label) => label.calculationBaseColumn === column)?.label ?? "",
                ]),
            ) as Record<CalculationBaseColumn, string>,
        [labels],
    );

    const isDirty = useMemo(
        () =>
            EDITABLE_COLUMNS.some((column) => {
                const draft = (drafts[column] ?? "").trim();
                const saved = (savedLabels[column] ?? "").trim();
                return draft !== saved;
            }),
        [drafts, savedLabels],
    );

    const handleDraftChange = (column: CalculationBaseColumn, value: string) => {
        setDrafts((prev) => ({ ...prev, [column]: value }));
    };

    const handleSaveAll = async () => {
        const changes = EDITABLE_COLUMNS.flatMap((column) => {
            const draft = (drafts[column] ?? "").trim();
            const saved = (savedLabels[column] ?? "").trim();
            if (draft === saved) return [];

            if (draft) {
                return [
                    dispatch(
                        upsertCalculationBaseColumnLabel({ calculationBaseColumn: column, label: draft, silent: true }),
                    ),
                ];
            }

            return [dispatch(deleteCalculationBaseColumnLabel({ calculationBaseColumn: column, silent: true }))];
        });

        if (changes.length === 0) return;

        setIsSaving(true);
        try {
            await Promise.all(changes.map((promise) => promise.unwrap()));
            toast.success(`${t("CalculationBaseColumnLabels.Store.SaveSuccess")}`);
        } catch {
            // Rejected thunks already surface API errors in the store.
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className={styles["calculation-base-labels-settings"]}>
            <h1>{t("CalculationBaseColumnLabels.Title")}</h1>
            <TitleSeparation
                title={t("CalculationBaseColumnLabels.SectionCalculation")}
                description={t("CalculationBaseColumnLabels.SectionCalculationDescription")}
                style={{ marginTop: "1.5rem" }}
            />
            <ol className={styles["calculation-steps"]}>
                {CALCULATION_STEPS.map(({ titleKey, descriptionKey }, index) => (
                    <li key={titleKey} className={styles["calculation-step"]}>
                        <span className={styles["step-number"]}>{index + 1}</span>
                        <div className={styles["step-content"]}>
                            <BodyHighlight>{t(`CalculationBaseColumnLabels.${titleKey}`)}</BodyHighlight>
                            <SmallBody color="grey">{t(`CalculationBaseColumnLabels.${descriptionKey}`)}</SmallBody>
                        </div>
                    </li>
                ))}
            </ol>
            <div className={styles["calculation-formulas"]}>
                <div className={styles["formula-card"]}>
                    <BodyHighlight>{t("CalculationBaseColumnLabels.QuantityFormulaTitle")}</BodyHighlight>
                    <SmallBody color="grey">{t("CalculationBaseColumnLabels.QuantityFormulaDescription")}</SmallBody>
                    <p className={styles["formula"]}>{t("CalculationBaseColumnLabels.QuantityFormula")}</p>
                </div>
                <div className={styles["formula-card"]}>
                    <BodyHighlight>{t("CalculationBaseColumnLabels.TurnoverFormulaTitle")}</BodyHighlight>
                    <SmallBody color="grey">{t("CalculationBaseColumnLabels.TurnoverFormulaDescription")}</SmallBody>
                    <p className={styles["formula"]}>{t("CalculationBaseColumnLabels.TurnoverFormula")}</p>
                </div>
            </div>
            <TitleSeparation
                title={t("CalculationBaseColumnLabels.SectionLabels")}
                description={t("CalculationBaseColumnLabels.Description")}
                style={{ marginTop: "2.5rem" }}
            />
            <SmallBody color="grey" className={styles["optional-columns-note"]}>
                {t("CalculationBaseColumnLabels.OptionalColumnsNote")}
            </SmallBody>
            <div className={styles["labels-list"]}>
                {CALCULATION_BASE_COLUMNS.map((column) => {
                    const isOptionalInactive =
                        labelsLoaded && OPTIONAL_LABEL_COLUMNS.has(column) && !(drafts[column] ?? "").trim();

                    return (
                        <div
                            key={column}
                            className={`${styles["label-row"]}${isOptionalInactive ? ` ${styles.inactive}` : ""}`}
                        >
                            <div className={styles["label-text"]}>
                                <div className={styles["label-title"]}>
                                    <BodyHighlight>{t(`CalculationBaseColumnLabels.Columns.${column}`)}</BodyHighlight>
                                    {isOptionalInactive && (
                                        <span className={styles["inactive-badge"]}>
                                            {t("CalculationBaseColumnLabels.InactiveBadge")}
                                        </span>
                                    )}
                                </div>
                                <SmallBody color="grey">{column}</SmallBody>
                            </div>
                            <StyledTextField
                                value={drafts[column] ?? ""}
                                placeholder={
                                    isOptionalInactive
                                        ? t("CalculationBaseColumnLabels.OptionalColumnPlaceholder")
                                        : t(`CalculationBaseColumnLabels.Columns.${column}`)
                                }
                                inputProps={{ maxLength: 200 }}
                                disabled={READ_ONLY_COLUMNS.has(column)}
                                onChange={(event) => handleDraftChange(column, event.target.value)}
                                sx={{ width: "20rem" }}
                            />
                        </div>
                    );
                })}
            </div>
            {(isDirty || isSaving) && (
                <SaveButton
                    buttonText={t("Settings.SaveChanges")}
                    onSave={handleSaveAll}
                    loading={isSaving}
                    disabled={isSaving}
                />
            )}
        </div>
    );
};

export default CalculationBaseColumnLabels;
